import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type StudioRowActionIcon =
  | "view"
  | "edit"
  | "add"
  | "delete"
  | "toggle"
  | "user"
  | "duplicate"
  | "tag"
  | "transfer"
  | "undo"
  | "more";

export type StudioRowAction = {
  key: string;
  label: string;
  icon: StudioRowActionIcon;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "primary" | "danger";
};

const ICONS: Record<StudioRowActionIcon, React.ReactNode> = {
  view: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M2.5 10s2.8-5 7.5-5 7.5 5 7.5 5-2.8 5-7.5 5-7.5-5-7.5-5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="10" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  ),
  edit: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M11.4 4.6 15.4 8.6M4 16l1.2-4.6L13.2 3.4a1.5 1.5 0 0 1 2.1 0l1.3 1.3a1.5 1.5 0 0 1 0 2.1L8.6 14.8 4 16Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  add: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M10 4.5v11M4.5 10h11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  ),
  delete: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M4.5 6h11M8 6V4.5h4V6M6.5 6l.6 9.2h5.8l.6-9.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  toggle: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <rect
        x="3"
        y="6.5"
        width="14"
        height="7"
        rx="3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <circle cx="13.2" cy="10" r="2.35" fill="currentColor" />
    </svg>
  ),
  user: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="6.5" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M4.5 16c.8-2.8 2.7-4.2 5.5-4.2s4.7 1.4 5.5 4.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  ),
  duplicate: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <rect
        x="6.5"
        y="6.5"
        width="9"
        height="9"
        rx="1.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M4.5 13.2V5.8A1.3 1.3 0 0 1 5.8 4.5h7.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  ),
  tag: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M3.6 9.4 9.4 3.6h5.2A1.4 1.4 0 0 1 16 5v5.2l-5.8 5.8a1.4 1.4 0 0 1-2 0L3.6 11.4a1.4 1.4 0 0 1 0-2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="12.2" cy="7.6" r="1.15" fill="currentColor" />
    </svg>
  ),
  transfer: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M4 6.5h9.5M10.2 3.8 13.5 6.5 10.2 9.2M16 13.5H6.5M9.8 16.2 6.5 13.5 9.8 10.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  undo: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="M7.2 6.2H3.8V2.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.2 6.2A6.2 6.2 0 1 1 3.8 10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  ),
  more: (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="5" cy="10" r="1.4" fill="currentColor" />
      <circle cx="10" cy="10" r="1.4" fill="currentColor" />
      <circle cx="15" cy="10" r="1.4" fill="currentColor" />
    </svg>
  ),
};

type StudioRowActionsProps = {
  actions: StudioRowAction[];
  maxVisible?: number;
  showLabel?: boolean;
};

const PHONE_ACTIONS = "(max-width: 639px)";

const isPhoneActions = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(PHONE_ACTIONS).matches;

type TipState = { label: string; x: number; y: number; below: boolean };

const StudioRowActions = ({ actions, maxVisible, showLabel }: StudioRowActionsProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState<TipState | null>(null);
  const visible = maxVisible == null ? actions : actions.slice(0, maxVisible);
  const overflow = maxVisible == null ? [] : actions.slice(maxVisible);

  const showTip = (event: React.SyntheticEvent<HTMLButtonElement>, label: string) => {
    if (showLabel && !isPhoneActions()) {
      return;
    }
    const box = event.currentTarget.getBoundingClientRect();
    setTip({
      label,
      x: box.left + box.width / 2,
      y: box.top,
      below: box.top < 48,
    });
  };

  const hideTip = () => setTip(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDoc = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (actions.length === 0) {
    return null;
  }

  return (
    <div
      className={`studio-row-actions${showLabel ? " studio-row-actions--labeled" : ""}`}
      ref={rootRef}
    >
      {visible.map((action) => (
        <button
          key={action.key}
          type="button"
          className={`studio-row-actions__btn${action.tone === "primary" ? " is-primary" : ""}${
            action.tone === "danger" ? " is-danger" : ""
          }`}
          aria-label={action.label}
          disabled={action.disabled}
          onMouseEnter={(event) => showTip(event, action.label)}
          onMouseLeave={hideTip}
          onFocus={(event) => showTip(event, action.label)}
          onBlur={hideTip}
          onClick={() => {
            hideTip();
            action.onClick();
          }}
        >
          <span className="studio-row-actions__icon">{ICONS[action.icon]}</span>
          {showLabel ? <span className="studio-row-actions__label">{action.label}</span> : null}
        </button>
      ))}
      {overflow.length > 0 ? (
        <>
          <button
            type="button"
            className={`studio-row-actions__btn${open ? " is-on" : ""}`}
            aria-label="More actions"
            aria-expanded={open}
            aria-haspopup="menu"
            onMouseEnter={(event) => showTip(event, "More actions")}
            onMouseLeave={hideTip}
            onFocus={(event) => showTip(event, "More actions")}
            onBlur={hideTip}
            onClick={() => {
              hideTip();
              setOpen((value) => !value);
            }}
          >
            {ICONS.more}
          </button>
          {open ? (
            <div className="studio-row-actions__menu" role="menu">
              {overflow.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  role="menuitem"
                  className="studio-row-actions__item"
                  disabled={action.disabled}
                  onClick={() => {
                    setOpen(false);
                    action.onClick();
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
      {tip && typeof document !== "undefined"
        ? createPortal(
            <div
              className={`studio-row-actions__tip${tip.below ? " is-below" : ""}`}
              style={{ left: tip.x, top: tip.below ? tip.y + 40 : tip.y }}
              role="tooltip"
            >
              {tip.label}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default StudioRowActions;
